import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["content", "actions"]
  static values = { url: String }

  connect() {
    this.startX = 0
    this.startY = 0
    this.currentX = 0
    this.isTracking = false
    this.isOpen = false
    this.directionLocked = false

    this.boundTouchStart = this.onTouchStart.bind(this)
    this.boundTouchMove = this.onTouchMove.bind(this)
    this.boundTouchEnd = this.onTouchEnd.bind(this)

    this.contentTarget.addEventListener("touchstart", this.boundTouchStart, { passive: true })
    this.contentTarget.addEventListener("touchmove", this.boundTouchMove, { passive: false })
    this.contentTarget.addEventListener("touchend", this.boundTouchEnd, { passive: true })
  }

  disconnect() {
    this.contentTarget.removeEventListener("touchstart", this.boundTouchStart)
    this.contentTarget.removeEventListener("touchmove", this.boundTouchMove)
    this.contentTarget.removeEventListener("touchend", this.boundTouchEnd)
  }

  onTouchStart(e) {
    const touch = e.touches[0]
    this.startX = touch.clientX
    this.startY = touch.clientY
    this.currentX = 0
    this.isTracking = true
    this.directionLocked = false
    this.contentTarget.style.transition = "none"
  }

  onTouchMove(e) {
    if (!this.isTracking) return

    const touch = e.touches[0]
    const deltaX = touch.clientX - this.startX
    const deltaY = touch.clientY - this.startY

    if (!this.directionLocked) {
      if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
        this.isTracking = false
        this.contentTarget.style.transition = ""
        return
      }
      if (Math.abs(deltaX) > 5) {
        this.directionLocked = true
        this.actionsTarget.classList.remove("hidden")
      } else {
        return
      }
    }

    e.preventDefault()

    let offset = deltaX
    if (this.isOpen) {
      offset = deltaX - this.revealWidth()
    }

    // Only allow swiping left (negative offset)
    if (offset > 0) offset = 0

    this.currentX = offset
    this.contentTarget.style.transform = `translateX(${offset}px)`
    this.actionsTarget.style.width = `${Math.abs(offset)}px`
  }

  onTouchEnd() {
    if (!this.isTracking || !this.directionLocked) {
      this.isTracking = false
      if (!this.directionLocked && this.isOpen) {
        // Tap while open — close it
        this.close()
      }
      return
    }

    this.isTracking = false
    this.contentTarget.style.transition = "transform 0.3s ease"

    const width = this.element.offsetWidth
    const absOffset = Math.abs(this.currentX)

    if (absOffset > width * 0.4) {
      this.slideOutAndDelete()
    } else if (absOffset > this.revealWidth() * 0.5) {
      this.open()
    } else {
      this.close()
    }
  }

  revealWidth() {
    return 80
  }

  open() {
    this.isOpen = true
    this.contentTarget.style.transition = "transform 0.3s ease"
    this.contentTarget.style.transform = `translateX(-${this.revealWidth()}px)`
    this.actionsTarget.style.transition = "width 0.3s ease"
    this.actionsTarget.style.width = `${this.revealWidth()}px`
  }

  close() {
    this.isOpen = false
    this.contentTarget.style.transition = "transform 0.3s ease"
    this.contentTarget.style.transform = "translateX(0)"
    this.actionsTarget.style.transition = "width 0.3s ease"
    this.actionsTarget.style.width = "0px"
    setTimeout(() => {
      if (!this.isOpen) this.actionsTarget.classList.add("hidden")
    }, 300)
  }

  delete() {
    this.slideOutAndDelete()
  }

  slideOutAndDelete() {
    const width = this.element.offsetWidth
    this.contentTarget.style.transition = "transform 0.3s ease"
    this.contentTarget.style.transform = `translateX(-${width}px)`
    this.actionsTarget.style.transition = "width 0.3s ease"
    this.actionsTarget.style.width = `${width}px`

    setTimeout(() => {
      this.element.style.transition = "height 0.3s ease, opacity 0.3s ease"
      this.element.style.height = this.element.offsetHeight + "px"
      // Force reflow
      this.element.offsetHeight
      this.element.style.height = "0"
      this.element.style.opacity = "0"
      this.element.style.overflow = "hidden"

      setTimeout(() => {
        this.sendDelete()
        this.element.remove()
      }, 300)
    }, 300)
  }

  sendDelete() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
    fetch(this.urlValue, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "application/json"
      }
    })
  }
}
